'use client';

import { useState } from 'react';
import { format } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { Coins, Loader2, MinusCircle, PlusCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useAiCreditBalance, useGrantAiCredits, type AiCreditBalanceOutput } from '@/lib/api/account-manager';

type LedgerEntry = AiCreditBalanceOutput['ledger'][number];

interface AiCreditsTabProps {
  accountId: string | undefined;
}

export function AiCreditsTab({ accountId }: AiCreditsTabProps) {
  const { t } = useTranslation('common');
  // The grant hook invalidates the getAiCreditBalance query on success, which
  // refetches the balance — no explicit refetch() needed here (would double-fire).
  const { data, isLoading } = useAiCreditBalance(accountId);
  const grant = useGrantAiCredits(() => {
    setAmount('');
    setReason('');
  });

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const parsedAmount = Number.parseInt(amount, 10);
  const isAmountValid = Number.isInteger(parsedAmount) && parsedAmount !== 0;
  const isReasonValid = reason.trim().length > 0;
  const canSubmit = !!accountId && isAmountValid && isReasonValid && !grant.isPending;

  const handleSubmit = () => {
    if (!accountId || !isAmountValid || !isReasonValid) return;
    grant.mutate({ accountId, amount: parsedAmount, reason: reason.trim() });
  };

  if (!accountId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('accountManager.aiCredits.loadingAccount')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            {t('accountManager.aiCredits.title')}
          </CardTitle>
          <CardDescription className="max-w-2xl">
            {t('accountManager.aiCredits.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading || !data ? (
            <Skeleton className="h-20 w-full" />
          ) : (
            <div className="grid gap-4 sm:grid-cols-3">
              <SummaryStat label={t('accountManager.aiCredits.balance')} value={data.balance} highlight />
              <SummaryStat
                label={t('accountManager.aiCredits.lifetimePurchased')}
                value={data.lifetimePurchased}
              />
              <SummaryStat label={t('accountManager.aiCredits.lifetimeUsed')} value={data.lifetimeUsed} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('accountManager.aiCredits.adjustTitle')}</CardTitle>
          <CardDescription>{t('accountManager.aiCredits.adjustDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ai-credits-amount">{t('accountManager.aiCredits.amountLabel')}</Label>
            <Input
              id="ai-credits-amount"
              type="number"
              step="1"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder={t('accountManager.aiCredits.amountPlaceholder')}
              disabled={grant.isPending}
            />
            <p className="text-xs text-muted-foreground">{t('accountManager.aiCredits.amountHint')}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ai-credits-reason">{t('accountManager.aiCredits.reasonLabel')}</Label>
            <Textarea
              id="ai-credits-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('accountManager.aiCredits.reasonPlaceholder')}
              rows={2}
              maxLength={200}
              disabled={grant.isPending}
            />
          </div>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {grant.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('accountManager.aiCredits.submitting')}
              </>
            ) : isAmountValid && parsedAmount < 0 ? (
              <>
                <MinusCircle className="mr-2 h-4 w-4" />
                {t('accountManager.aiCredits.clawbackButton')}
              </>
            ) : (
              <>
                <PlusCircle className="mr-2 h-4 w-4" />
                {t('accountManager.aiCredits.grantButton')}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {data && data.ledger.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('accountManager.aiCredits.historyTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {data.ledger.map((entry) => (
                <LedgerRow key={entry.id} entry={entry} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryStat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={highlight ? 'mt-1 text-2xl font-semibold' : 'mt-1 text-2xl font-medium'}>
        {value.toLocaleString()}
      </p>
    </div>
  );
}

function LedgerRow({ entry }: { entry: LedgerEntry }) {
  const { t, i18n } = useTranslation('common');
  const dateLocale = i18n.language?.startsWith('fr') ? fr : enUS;
  const isPositive = entry.amount >= 0;

  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            {t(`accountManager.aiCredits.ledgerType.${entry.type}`, { defaultValue: entry.type })}
          </Badge>
          {entry.description && <span className="truncate text-sm">{entry.description}</span>}
        </div>
        {entry.createdAt && (
          <p className="mt-1 text-xs text-muted-foreground">
            {format(new Date(entry.createdAt), 'PPp', { locale: dateLocale })}
          </p>
        )}
      </div>
      <div className="shrink-0 text-right">
        <p className={isPositive ? 'font-medium text-emerald-600' : 'font-medium text-red-600'}>
          {isPositive ? '+' : ''}
          {entry.amount.toLocaleString()}
        </p>
        <p className="text-xs text-muted-foreground">{entry.balanceAfter.toLocaleString()}</p>
      </div>
    </div>
  );
}
